import type { PublicPost } from './post-service';

export function videoPostsFromPosts(
  posts: PublicPost[],
  initialPost?: PublicPost | null,
) {
  const videoPosts = posts.filter((post) => post.media?.kind === 'video');

  if (!initialPost || initialPost.media?.kind !== 'video') {
    return videoPosts;
  }

  const initialIndex = videoPosts.findIndex(
    (post) => post.id === initialPost.id,
  );

  if (initialIndex >= 0) {
    return [
      videoPosts[initialIndex],
      ...videoPosts.slice(initialIndex + 1),
      ...videoPosts.slice(0, initialIndex),
    ];
  }

  return [
    initialPost,
    ...videoPosts.filter((post) => post.id !== initialPost.id),
  ];
}

export function mergeVideoViewerQueue(
  currentQueue: PublicPost[],
  posts: PublicPost[],
  initialPost?: PublicPost | null,
) {
  const nextQueue = videoPostsFromPosts(posts);

  if (currentQueue.length === 0) {
    return videoPostsFromPosts(posts, initialPost);
  }

  const nextById = new Map(nextQueue.map((post) => [post.id, post]));
  const preserveSeededInitialPost =
    initialPost?.media?.kind === 'video' && !nextById.has(initialPost.id);
  const seenIds = new Set<string>();
  let changed = false;

  const mergedQueue = currentQueue.flatMap((currentPost) => {
    seenIds.add(currentPost.id);

    const nextPost = nextById.get(currentPost.id);

    if (!nextPost) {
      if (preserveSeededInitialPost && currentPost.id === initialPost?.id) {
        return [currentPost];
      }

      changed = true;
      return [];
    }

    if (nextPost !== currentPost) {
      changed = true;
    }

    return [nextPost];
  });

  for (const nextPost of nextQueue) {
    if (seenIds.has(nextPost.id)) {
      continue;
    }

    mergedQueue.push(nextPost);
    changed = true;
  }

  return changed ? mergedQueue : currentQueue;
}

export function videoViewerIndexForScrollOffset({
  height,
  offsetY,
  queueLength,
  requireSettledOffset = false,
}: {
  height: number;
  offsetY: number;
  queueLength: number;
  requireSettledOffset?: boolean;
}) {
  if (height <= 0 || queueLength === 0) {
    return null;
  }

  const rawIndex = offsetY / height;
  const roundedIndex = Math.round(rawIndex);

  if (requireSettledOffset && Math.abs(rawIndex - roundedIndex) > 0.02) {
    return null;
  }

  return Math.max(0, Math.min(queueLength - 1, roundedIndex));
}

export function videoPostIndexForPostId(
  videoPosts: PublicPost[],
  postId: string | null,
) {
  return Math.max(
    0,
    videoPosts.findIndex((post) => post.id === postId),
  );
}
